import React, { useEffect, useState } from "react";
import { api } from "../../../software/api";
import { Loader2 } from "lucide-react";

const dmy = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "");
const thisMonth = () => new Date().toLocaleDateString("en-CA").slice(0, 7);
const th = "px-2 py-1.5 text-left text-[11px] font-black uppercase text-white bg-[#1a4f8a]";
const td = "px-2 py-1.5 text-[13px] border-b border-slate-200";

function useLoad(path) {
  const [d, setD] = useState(null), [err, setErr] = useState("");
  useEffect(() => { setD(null); setErr(""); api("GET", path).then(setD).catch((e) => setErr(e.message)); }, [path]);
  return [d, err];
}

const Busy = ({ err }) => <div className="p-8 text-center text-slate-500">{err ? <span className="text-red-700 font-bold" data-testid="office-report-error">{err}</span> : <Loader2 className="h-6 w-6 animate-spin mx-auto" />}</div>;

const Tally = ({ title, items, k, testid }) => (
  <div className="bg-white border border-[#9db8d6] rounded-lg overflow-hidden" data-testid={testid}>
    <div className="bg-[#dbe7f5] px-3 py-1.5 font-black text-[#123a63] text-[13px]">{title}</div>
    {items.length ? items.map((x) => (
      <div key={x[k]} className="flex justify-between px-3 py-1.5 text-[13px] border-t border-slate-100"><span>{x[k]}</span><b data-testid={`${testid}-${x[k]}`}>{x.count}</b></div>
    )) : <div className="px-3 py-2 text-[13px] text-slate-400">None</div>}
  </div>
);

export function RunbackReport({ onOpenWo }) {
  const [month, setMonth] = useState(thisMonth());
  const [d, err] = useLoad(`/reports/runbacks?month=${month}`);
  return (
    <div className="h-full overflow-auto p-4 bg-[#eaf1fb] space-y-3" data-testid="runback-report">
      <div className="flex items-center gap-3">
        <label className="text-[13px] font-bold text-[#123a63]">Month</label>
        <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="runback-month" className="h-8 rounded border border-[#9db8d6] px-2 text-[13px]" />
        {d && <span className="text-[13px] font-black text-[#123a63]" data-testid="runback-total">{d.total} runback{d.total === 1 ? "" : "s"}</span>}
      </div>
      {!d ? <Busy err={err} /> : <>
        <div className="grid grid-cols-2 gap-3 max-w-3xl">
          <Tally title="Runbacks per tech" items={d.by_tech} k="tech" testid="runback-by-tech" />
          <Tally title="Most common reasons" items={d.reasons} k="reason" testid="runback-reasons" />
        </div>
        <table className="w-full bg-white border border-[#9db8d6]" data-testid="runback-rows">
          <thead><tr>{["Date", "Tech", "Client", "W/O", "Return day", "Reason"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody>{d.rows.map((r) => (
            <tr key={r.id} onClick={() => onOpenWo(r.client_id, r.wo_id)} data-testid={`runback-row-${r.id}`} className="cursor-pointer hover:bg-[#f3f7fc]">
              <td className={td}>{dmy(r.date)}</td><td className={td}>{r.tech}</td><td className={td}>{r.client_name}</td>
              <td className={td}>{r.wo_number || ""}</td><td className={td}>{dmy(r.runback_to)}</td><td className={td}>{r.reason || <span className="text-slate-400">No reason given</span>}</td>
            </tr>
          ))}</tbody>
        </table>
        {!d.rows.length && <div className="text-center text-slate-500 text-[13px]" data-testid="runback-empty">No runbacks this month.</div>}
      </>}
    </div>
  );
}

const Jobs = ({ title, rows, kind, onOpenWo }) => (
  <div className="space-y-1">
    <div className="font-black text-[#123a63]" data-testid={`checklist-${kind}-title`}>{title} ({rows.length})</div>
    <table className="w-full bg-white border border-[#9db8d6]">
      <thead><tr>{["Date", "W/O", "Client", "Tech", "Job", "Last Result line"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id} onClick={() => onOpenWo(r.client_id, r.id)} data-testid={`checklist-row-${r.id}`} className="cursor-pointer hover:bg-[#f3f7fc]">
          <td className={td}>{dmy(r.date)}</td><td className={td}>{r.wo_number}</td><td className={`${td} font-bold`}>{r.client_name}</td><td className={td}>{r.technician}</td>
          <td className={td}>{r.description}</td>
          <td className={`${td} text-slate-500 max-w-[420px] truncate`} title={r.last_result}>{r.last_result}</td>
        </tr>
      ))}</tbody>
    </table>
    {!rows.length && <div className="text-[13px] text-slate-500 px-1">Nothing waiting.</div>}
  </div>
);

export function KevinChecklist({ onOpenWo }) {
  const [d, err] = useLoad("/reports/kevin-checklist");
  return (
    <div className="h-full overflow-auto p-4 bg-[#eaf1fb] space-y-5" data-testid="kevin-checklist">
      {!d ? <Busy err={err} /> : <>
        <Jobs title="Completed - waiting to be invoiced" rows={d.invoice} kind="invoice" onOpenWo={onOpenWo} />
        <Jobs title="Quotations - waiting to be priced" rows={d.quote} kind="quote" onOpenWo={onOpenWo} />
      </>}
    </div>
  );
}
